import {
  mapHelpArticleDetailDto,
  mapHelpArticleListItemDto,
  mapHelpCategoryDto,
  type HelpArticleDetail,
  type HelpArticleDetailDto,
  type HelpArticleListItem,
  type HelpArticleListItemDto,
  type HelpCategory,
  type HelpCategoryDto,
  type ListResultDto,
} from '@/types';

import type { HelpListArticlesOptions, HelpService } from '../helpService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`HelpService`(T5 / docs/50,Phase 1 read-only)。
 * 全走 `httpRequestWithRefresh`;`/api/help` 在 `GROUP_HEADER_EXEMPT_PATTERNS` → 不帶
 * `X-Current-Group`(非 group-scoped)。
 */

const PATH = {
  categories: '/api/help/categories',
  articles: '/api/help/articles',
  article: (slug: string) => `/api/help/articles/${encodeURIComponent(slug)}`,
} as const;

/** 只 append 有帶的 query;`popular` 僅 true 時帶(false = 預設序,不送)。 */
function buildArticlesPath(opts?: HelpListArticlesOptions): string {
  const qs = new URLSearchParams();
  if (opts?.category) qs.set('category', opts.category);
  if (opts?.q) qs.set('q', opts.q);
  if (opts?.popular) qs.set('popular', 'true');
  if (opts?.limit !== undefined) qs.set('limit', String(opts.limit));
  const query = qs.toString();
  return query ? `${PATH.articles}?${query}` : PATH.articles;
}

export function createHttpHelpService(config: HttpClientConfig): HelpService {
  return {
    async getCategories(): Promise<HelpCategory[]> {
      const env = await httpRequestWithRefresh<ListResultDto<HelpCategoryDto>>(
        config,
        'GET',
        PATH.categories,
      );
      const items = env.result?.items ?? [];
      return items.map(mapHelpCategoryDto);
    },

    async listArticles(opts?: HelpListArticlesOptions): Promise<HelpArticleListItem[]> {
      const env = await httpRequestWithRefresh<ListResultDto<HelpArticleListItemDto>>(
        config,
        'GET',
        buildArticlesPath(opts),
      );
      const items = env.result?.items ?? [];
      return items.map(mapHelpArticleListItemDto);
    },

    async getArticle(slug: string): Promise<HelpArticleDetail> {
      const env = await httpRequestWithRefresh<HelpArticleDetailDto>(
        config,
        'GET',
        PATH.article(slug),
      );
      return mapHelpArticleDetailDto(unwrapResult(env, 'help.getArticle'));
    },
  };
}
